class NativeInfiniteSlider {
  constructor(container, options = {}) {
    if (!container || container.dataset.sliderInitialized === 'true') return;
    
    this.container = container;
    this.container.dataset.sliderInitialized = 'true';
    
    this.options = Object.assign({
      trackSelector: '.slider-track',
      arrowLeftSelector: '.arrow-left',
      arrowRightSelector: '.arrow-right',
      pointSelector: '.slider-point',
      progressSelector: '.point-progress',
      videoSelector: 'video',
      loop: true,
      autoplay: true,
      pauseOnHover: false,
      defaultInterval: 5000,
      intervalAttribute: 'data-interval',
      dragThreshold: 0.15,
      speed: 400,
      onMounted: null,
      onChange: null,
    }, options);

    this.track = this.container.querySelector(this.options.trackSelector);
    if (!this.track) return;

    this.originalSlides = Array.from(this.track.children).filter(el => !el.classList.contains('is-clone'));
    this.originalTotalSlides = this.originalSlides.length;
    if (this.originalTotalSlides <= 1) return;

    this.track.style.position = 'relative';

    this.state = {
      currentIndex: 0,
      currentTranslate: 0,
      isAnimating: false,
      isHidden: false,
      isInitialized: false, 
      windowWidth: window.innerWidth, 
      playTimeoutId: null, // 替代 rAF
      isPlaying: false,
      isHovered: false,
      isInViewport: true,
      prevIsAtStart: null,
      prevIsAtEnd: null,
      prevActivePointIndex: null
    };

    this.cache = {
      viewportWidth: 0,
      scrollWidth: 0,
      maxTranslate: 0,
      slideWidth: 300,
      offsets: []
    };

    this.dom = {
      leftArrows: Array.from(this.container.querySelectorAll(this.options.arrowLeftSelector)),
      rightArrows: Array.from(this.container.querySelectorAll(this.options.arrowRightSelector)),
      points: Array.from(this.container.querySelectorAll(this.options.pointSelector)),
      progressBars: []
    };

    this.dom.points.forEach(p => {
      const bar = p.querySelector(this.options.progressSelector);
      if (bar) this.dom.progressBars.push(bar);
    });

    this._handleResize = this._handleResize.bind(this);
    this._wakeUpHandler = this._wakeUpHandler.bind(this);
    this._delegatedClickHandler = this._delegatedClickHandler.bind(this);

    this._handleVisibilityChange = this._handleVisibilityChange.bind(this);

    this._observer = null;
    
    this.mount();
  }

  _measure() {
    if (!this.track || this.track.offsetParent === null) {
      this.state.isHidden = true;
      return false;
    }
    this.state.isHidden = false;
    
    // 【新增】：检测当前容器是否为 RTL 排版
    this.isRTL = window.getComputedStyle(this.container).direction === 'rtl';

    this.cache.viewportWidth = this.track.parentElement.clientWidth;
    this.cache.scrollWidth = this.track.scrollWidth;
    this.cache.maxTranslate = Math.max(0, this.cache.scrollWidth - this.cache.viewportWidth);
    this.cache.slideWidth = this.slides[0] ? this.slides[0].offsetWidth : 300;
    
    this.cache.offsets = this.slides.map(s => s.offsetLeft);
    return true;
  }

  _yield() {
    return new Promise(resolve => setTimeout(resolve, 0));
  }

  _optimizeClones(clone) {
    clone.querySelectorAll('img').forEach(img => {
      img.setAttribute('loading', 'lazy');
      img.setAttribute('decoding', 'async');
    });
  }

  mount() {
    if (this.track.offsetParent === null) {
      this.state.isHidden = true;
      this.state.isInitialized = false;
      window.addEventListener('resize', this._wakeUpHandler, { passive: true });
      return; 
    }
    this._initCore();
  }

  _wakeUpHandler() {
    if (this.track.offsetParent !== null && !this.state.isInitialized) {
      window.removeEventListener('resize', this._wakeUpHandler);
      this._initCore();
    }
  }

  async _initCore() {
    this.state.isInitialized = true;
    await this._yield();

    this.track.querySelectorAll('.is-clone').forEach(el => el.remove());
    this.cloneCount = 0;
    
    if (this.options.loop) {
      this.cloneCount = this.options.clones === 'full' ? this.originalTotalSlides : (parseInt(this.options.clones) || 1);
      
      const fragBack = document.createDocumentFragment();
      for (let i = 0; i < this.cloneCount; i++) {
        const clone = this.originalSlides[i % this.originalTotalSlides].cloneNode(true);
        clone.classList.add('is-clone');
        this._optimizeClones(clone);
        fragBack.appendChild(clone);
        if (i % 2 === 0) await this._yield(); 
      }
      this.track.appendChild(fragBack);
      
      const fragFront = document.createDocumentFragment();
      for (let i = 0; i < this.cloneCount; i++) {
        let negativeIdx = -this.cloneCount + i;
        let index = (this.originalTotalSlides + (negativeIdx % this.originalTotalSlides)) % this.originalTotalSlides;
        
        const clone = this.originalSlides[index].cloneNode(true);
        clone.classList.add('is-clone');
        this._optimizeClones(clone);
        fragFront.appendChild(clone);
        if (i % 2 === 0) await this._yield(); 
      }
      this.track.insertBefore(fragFront, this.track.firstChild);
    }
    
    await this._yield(); 

    this.slides = Array.from(this.track.children);
    this.state.currentIndex = this.cloneCount; 

    // 只在这里和 Resize 时调用 _measure
    const isVisible = this._measure();

    if (this.options.autoplay && 'IntersectionObserver' in window) {
      this._observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.state.isInViewport = entry.isIntersecting;
          if (entry.isIntersecting) {
            if (this.options.autoplay && !document.hidden && !this.state.isHovered && !this.state.isAnimating) {
              this.play();
            }
          } else {
            this.pause();
          }
        });
      }, { threshold: 0.01 });
      this._observer.observe(this.container);
    }

    this._bindEvents(); 
    
    if (isVisible) {
      this._setupDrag(); // 只绑定一次 Drag 事件！
      this._goToIndex(this.state.currentIndex, true);
    }
    
    if (typeof this.options.onMounted === 'function') {
      this.options.onMounted(this);
    }
  }

  go(control) {
    if (this.state.isAnimating || this.state.isHidden) return; 
    
    let isAtStart = false;
    let isAtEnd = false;
    
    if (!this.options.loop) {
      isAtStart = this.state.currentTranslate >= -1;
      isAtEnd = Math.abs(this.state.currentTranslate) >= this.cache.maxTranslate - 1;
    }

    if (typeof control === 'number') {
      let target = control + this.cloneCount;
      if (!this.options.loop) target = Math.max(0, Math.min(target, this.originalTotalSlides - 1));
      this._goToIndex(target);
    } else if (typeof control === 'string') {
      if (control === '+1') {
        if (!this.options.loop && isAtEnd) return; 
        this._goToIndex(this.state.currentIndex + 1);
      } else if (control === '-1') {
        if (!this.options.loop && isAtStart) return; 
        this._goToIndex(this.state.currentIndex - 1);
      } else if (control.startsWith('>')) {
        let targetIndex = parseInt(control.slice(1));
        if (!isNaN(targetIndex)) {
          targetIndex = Math.max(0, Math.min(targetIndex, this.originalTotalSlides - 1));
          this._goToIndex(targetIndex + this.cloneCount);
        }
      }
    }
  }

  _goToIndex(index, instant = false) {
    if (this.state.isHidden || !this.slides[index]) return;
    if (this.state.currentIndex === index && !instant && this.state.isAnimating) return;

    this.state.isAnimating = !instant;
    this.pause(); // 切换时暂停，重置进度条
    
    this.state.currentIndex = index;

    let offset = this.cache.offsets[index] || 0;
    if (!this.options.loop) {
      offset = Math.min(offset, this.cache.maxTranslate);
    }

    this.state.currentTranslate = -offset;
    
    this.track.style.transition = instant ? 'none' : `transform ${this.options.speed}ms cubic-bezier(0.25, 1, 0.5, 1)`;
    this.track.style.transform = `translate3d(${this.state.currentTranslate}px, 0, 0)`;

    this._updateUI(); 
    this._handleVideos(this.state.currentIndex, this.state.currentIndex);
    
    if (typeof this.options.onChange === 'function' && !instant) {
      this.options.onChange(this.getRealIndex(this.state.currentIndex), this);
    }
    
    if (!instant) {
      clearTimeout(this.state.transitionTimeout);
      this.state.transitionTimeout = setTimeout(() => {
        this.state.isAnimating = false;
        
        if (this.options.loop) {
          if (this.state.currentIndex < this.cloneCount) {
            this.state.currentIndex += this.originalTotalSlides;
            this._goToIndex(this.state.currentIndex, true);
            return; 
          } else if (this.state.currentIndex >= this.cloneCount + this.originalTotalSlides) {
            this.state.currentIndex -= this.originalTotalSlides;
            this._goToIndex(this.state.currentIndex, true);
            return; 
          }
        }

        if (this.options.autoplay && !document.hidden && this.state.isInViewport && !this.state.isHovered) {
          this.play();
        }
      }, this.options.speed + 20); 
    } else {
      this.state.isAnimating = false;
      if (this.options.autoplay && !document.hidden && this.state.isInViewport && !this.state.isHovered) {
        this.play();
      }
    }
  }

  _handleVideos(newIdx, oldIdx) {
    // [无变更，保持原样]
    if (oldIdx !== null && this.slides[oldIdx]) {
      const oldVids = this.slides[oldIdx].querySelectorAll(this.options.videoSelector);
      oldVids.forEach(v => { v.pause(); v.currentTime = 0; });
    }
    if (this.slides[newIdx]) {
      const newVids = this.slides[newIdx].querySelectorAll(this.options.videoSelector);
      newVids.forEach(v => { v.play().catch(() => {}); });
    }
  }

  _updateUI() {
    if (!this.options.loop) {
      const isAtStart = this.state.currentTranslate >= -1;
      const isAtEnd = Math.abs(this.state.currentTranslate) >= this.cache.maxTranslate - 1;

      if (this.state.prevIsAtStart !== isAtStart) {
        this.dom.leftArrows.forEach(btn => {
          btn.classList.toggle('disabled', isAtStart);
          btn.style.pointerEvents = isAtStart ? 'none' : ''; 
        });
        this.state.prevIsAtStart = isAtStart;
      }

      if (this.state.prevIsAtEnd !== isAtEnd) {
        this.dom.rightArrows.forEach(btn => {
          btn.classList.toggle('disabled', isAtEnd);
          btn.style.pointerEvents = isAtEnd ? 'none' : '';   
        });
        this.state.prevIsAtEnd = isAtEnd;
      }
    }

    if (this.dom.points.length > 0) {
      const realIdx = this.getRealIndex(this.state.currentIndex);
      if (this.state.prevActivePointIndex !== realIdx) {
        this.dom.points.forEach((p, idx) => {
          const pointIndex = idx % this.originalTotalSlides; 
          if (pointIndex === realIdx) {
            p.classList.add('active');
          } else {
            p.classList.remove('active');
            const bar = p.querySelector(this.options.progressSelector);
            if (bar) {
              // 重置进度条时不带动画
              bar.style.transition = 'none';
              bar.style.width = '0%';
            }
          }
        });
        this.state.prevActivePointIndex = realIdx;
      }
    }
  }

  _setupDrag() {
    // 确保仅执行一次
    if (this._dragInitialized) return;
    this._dragInitialized = true;

    let startX = 0, startY = 0, currentX = 0;
    let isDragging = false, isScrolling = null, userHasDragged = false;
    let dragRafId = null; 

    const getXY = (e) => {
      if (e.touches && e.touches.length > 0) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      if (e.changedTouches && e.changedTouches.length > 0) return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
      return { x: e.clientX, y: e.clientY };
    };

    const dragMove = (e) => {
      if (!isDragging || this.state.isHidden) return;
      
      const pos = getXY(e);
      currentX = pos.x;
      let diffX = currentX - startX;
      const diffY = pos.y - startY;

      const isMouse = e.type.includes('mouse') || e.pointerType === 'mouse';

      if (isScrolling === null) {
        if (isMouse) {
          isScrolling = false; 
        } else {
          if (Math.abs(diffX) > 3 || Math.abs(diffY) > 3) {
            isScrolling = Math.abs(diffY) > Math.abs(diffX);
          }
        }
      }

      if (isScrolling === true || isScrolling === null) return; 

      if (e.cancelable && !isMouse) e.preventDefault();
      if (Math.abs(diffX) > 5) userHasDragged = true;

      let translate = this.state.currentTranslate + diffX;

      if (!this.options.loop) {
        const maxTranslate = this.cache.maxTranslate;
        if (translate > 0) {
          translate = diffX * 0.3; 
        } else if (translate < -maxTranslate) {
          const overscroll = Math.abs(translate) - maxTranslate;
          translate = -maxTranslate - (overscroll * 0.3);
        }
      }

      if (!dragRafId) {
        dragRafId = requestAnimationFrame(() => {
          this.track.style.transform = `translate3d(${translate}px, 0, 0)`;
          dragRafId = null;
        });
      }
    };

    // 找到 _setupDrag() 中的 dragEnd 定义，替换如下内容：
    const dragEnd = (e) => {
        if (!isDragging) return;
        isDragging = false;
        
        if (dragRafId) {
          cancelAnimationFrame(dragRafId);
          dragRafId = null;
        }
        
        window.removeEventListener('touchmove', dragMove);
        window.removeEventListener('touchend', dragEnd);
        window.removeEventListener('mousemove', dragMove);
        window.removeEventListener('mouseup', dragEnd);

        if (!userHasDragged) {
          if (this.options.autoplay && !document.hidden && this.state.isInViewport && !this.state.isHovered) {
            this.play(); 
          }
          return;
        }
        
        const diffX = currentX - startX;
        // 【修改点】：根据 RTL 状态翻转逻辑滑动距离
        const logicalDiffX = this.isRTL ? -diffX : diffX;
        const threshold = Math.min(this.cache.slideWidth * this.options.dragThreshold, 100); 
        
        let isAtEnd = false;
        if (!this.options.loop && this.state.currentTranslate <= -this.cache.maxTranslate) {
            isAtEnd = true;
        }

        // 使用 logicalDiffX 来判断翻页
        if (logicalDiffX < -threshold) {
          if (!this.options.loop && (this.state.currentIndex >= this.originalTotalSlides - 1 || isAtEnd)) {
            this._goToIndex(this.state.currentIndex);
          } else {
            this.go('+1');
          }
        } else if (logicalDiffX > threshold) {
          if (!this.options.loop && this.state.currentIndex <= 0) {
            this._goToIndex(this.state.currentIndex);
          } else {
            this.go('-1');
          }
        } else {
          this._goToIndex(this.state.currentIndex);
        }
        
        setTimeout(() => { userHasDragged = false; }, 50);
    };

    const dragStart = (e) => {
      if (e.button === 2 || this.state.isAnimating || this.state.isHidden) return;
      
      isDragging = true;
      userHasDragged = false;
      isScrolling = null;
      
      const pos = getXY(e);
      startX = pos.x;
      startY = pos.y;
      currentX = startX;
      
      this.pause();
      this.track.style.transition = 'none';

      window.addEventListener('touchmove', dragMove, { passive: false });
      window.addEventListener('touchend', dragEnd);
      window.addEventListener('mousemove', dragMove, { passive: false });
      window.addEventListener('mouseup', dragEnd);
    };

    this.track.addEventListener('touchstart', dragStart, { passive: true });
    this.track.addEventListener('mousedown', dragStart);
    this.track.addEventListener('click', (e) => {
      if (userHasDragged) { e.preventDefault(); e.stopPropagation(); }
    }, true);
    this.track.addEventListener('dragstart', (e) => e.preventDefault());
  }

  _delegatedClickHandler(e) {
    if (e.target.closest(this.options.arrowLeftSelector)) {
      e.preventDefault();
      // 【修改点】：RTL 下左侧是“下一张”
      return this.go(this.isRTL ? '+1' : '-1');
    }
    if (e.target.closest(this.options.arrowRightSelector)) {
      e.preventDefault();
      // 【修改点】：RTL 下右侧是“上一张”
      return this.go(this.isRTL ? '-1' : '+1');
    }
    const point = e.target.closest(this.options.pointSelector);
    if (point) {
      e.preventDefault();
      const idx = this.dom.points.indexOf(point);
      if (idx > -1) {
        const targetIndex = idx % this.originalTotalSlides;
        if (this.getRealIndex(this.state.currentIndex) !== targetIndex) {
          this.go(`>${targetIndex}`);
        }
      }
    }
  }

  _bindEvents() {
    this.container.addEventListener('click', this._delegatedClickHandler);
    window.addEventListener('resize', this._handleResize, { passive: true });
    document.addEventListener('visibilitychange', this._handleVisibilityChange);
  }
  _handleVisibilityChange() {
    if (document.hidden) {
      this.pause();
    } else {
      if (this.options.autoplay && this.state.isInViewport && !this.state.isHidden && !this.state.isHovered) {
        this.play();
      }
    }
  }
  _handleResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      const newWidth = window.innerWidth;
      if (newWidth === this.state.windowWidth) return;
      this.state.windowWidth = newWidth;
      
      const isVisible = this._measure();
      if (isVisible) {
        // 移除了 this._setupDrag(); 防止重复绑定
        this._goToIndex(this.state.currentIndex, true);
      }
    }, 100);
  }

  getRealIndex(index) {
    if (!this.options.loop) return index; 
    let real = index - this.cloneCount;
    if (real < 0) real += this.originalTotalSlides;
    if (real >= this.originalTotalSlides) real -= this.originalTotalSlides;
    return real;
  }

  play() {
    if (this.state.isPlaying || this.state.isHidden || !this.options.autoplay) return; 
    
    this.pause();
    this.state.isPlaying = true;

    const currentSlide = this.slides[this.state.currentIndex];
    const duration = parseInt(currentSlide && currentSlide.getAttribute(this.options.intervalAttribute)) || this.options.defaultInterval;
    const realIdx = this.getRealIndex(this.state.currentIndex);
    
    // 【修复点】：遍历所有的 point，通过取余数匹配当前真实的 realIdx
    // 这样无论 HTML 复制了多少份（PC版/Mobile版），都能一起跑动画！
    this.dom.points.forEach((point, idx) => {
      if (idx % this.originalTotalSlides === realIdx) {
        const bar = point.querySelector(this.options.progressSelector);
        if (bar) {
          bar.style.transition = 'none';
          bar.style.width = '0%';
          void bar.offsetWidth; // 触发局部重排
          
          requestAnimationFrame(() => {
            bar.style.transition = `width ${duration}ms linear`;
            bar.style.width = '100%';
          });
        }
      }
    });

    this.state.playTimeoutId = setTimeout(() => {
      if (!this.options.loop && this.state.currentIndex >= this.originalTotalSlides - 1) {
        this.go('>0');
      } else {
        this.go('+1');
      }
    }, duration);
  }

  pause() {
    this.state.isPlaying = false;
    
    if (this.state.playTimeoutId) {
      clearTimeout(this.state.playTimeoutId);
      this.state.playTimeoutId = null;
    }

    const realIdx = this.getRealIndex(this.state.currentIndex);
    
    // 【修复点】：暂停时也要遍历所有对应的进度条
    this.dom.points.forEach((point, idx) => {
      if (idx % this.originalTotalSlides === realIdx) {
        const bar = point.querySelector(this.options.progressSelector);
        if (bar) {
          const computedWidth = window.getComputedStyle(bar).width;
          bar.style.transition = 'none';
          bar.style.width = computedWidth; // 锁定当前宽度
        }
      }
    });
  }

  destroy() {
    this.pause();
    clearTimeout(this.state.transitionTimeout);
    clearTimeout(this.resizeTimer);
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    window.removeEventListener('resize', this._handleResize);
    window.removeEventListener('resize', this._wakeUpHandler);
    this.container.removeEventListener('click', this._delegatedClickHandler);
    document.removeEventListener('visibilitychange', this._handleVisibilityChange);
    this.track.querySelectorAll('.is-clone').forEach(clone => clone.remove());
    this.track.style.transform = '';
    this.track.style.transition = '';
    delete this.container.dataset.sliderInitialized;
  }
}